import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Image from '@atlaskit/image/image';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import googleLogo from '../../../img/googleLogo.svg';

const ChoreographerModal = ({ children, onClose, testId }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width="medium" testId={testId}>
			{children}
		</Modal>
	);
};

const i18n = defineMessages({
	googleSignInModalTitle: {
		id: 'import-from-sources.import-from-gd-modal.title',
		defaultMessage: 'Import Google Doc',
		description:
			'Title of the modal dialog shown when user needs to authenticate with Google to import a document.',
	},
	googleSignInModalText: {
		id: 'import-from-sources.import-from-gd-modal.text',
		defaultMessage: 'Connect your Google account to continue',
		description:
			'Body text of the modal explaining that the user needs to sign in with their Google account to proceed with importing Google Docs.',
	},
	cancelGoogleSignInModalButtonLabel: {
		id: 'import-from-sources.import-from-gd-modal.cancelAction',
		defaultMessage: 'Cancel',
		description:
			'Label for the button that closes the Google sign-in modal without authenticating.',
	},
	signInGoogleSignInModalButtonLabel: {
		id: 'import-from-sources.import-from-gd-modal.singIn',
		defaultMessage: 'Sign in with Google',
		description:
			'Label for the button that initiates Google authentication process for importing Google Docs.',
	},
});

const styles = cssMap({
	content: {
		height: '110px',
	},
	contentText: {
		marginBlock: token('space.250'),
		marginInline: 0,
	},
	buttonGroup: {
		marginTop: token('space.250'),
	},
});

type GoogleSignInModalTypes = {
	onClose: () => any;
	onSignIn: () => any;
	handleAuth: () => void;
};

export const GoogleSignInModal: React.FC<GoogleSignInModalTypes> = ({
	onClose,
	onSignIn,
	handleAuth,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'importGoogleSignInModal',
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onCloseClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'importGDocModal',
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'importGoogleSignInCancel',
			},
		}).fire();

		return onClose();
	};

	const onSignInClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'importGDocModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'importGoogleSignIn',
			},
		}).fire();

		return onSignIn();
	};

	const onHandleAuthClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'importGDocModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'importGoogleSignInAuthClick',
			},
		}).fire();

		return handleAuth();
	};

	return (
		<ChoreographerModal onClose={onCloseClick} testId="import-google-sign-in-modal">
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<FormattedMessage {...i18n.googleSignInModalTitle} />
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<Box xcss={styles.content}>
					<Box as="p" xcss={styles.contentText}>
						<FormattedMessage {...i18n.googleSignInModalText} />
					</Box>
					<Inline alignInline="end" alignBlock="center" space="space.150" xcss={styles.buttonGroup}>
						<Button testId="google-sign-in-close-button" appearance="subtle" onClick={onCloseClick}>
							<FormattedMessage {...i18n.cancelGoogleSignInModalButtonLabel} />
						</Button>
						<Button
							testId="google-sign-in-button"
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							onClick={fg('cc_feature_discovery_ifs_fe') ? onHandleAuthClick : onSignInClick}
						>
							<Inline space="space.200">
								<Image src={googleLogo} alt="" height={18} width={18} />
								<Text weight="semibold" color="color.text.subtle">
									<FormattedMessage {...i18n.signInGoogleSignInModalButtonLabel} />
								</Text>
							</Inline>
						</Button>
					</Inline>
				</Box>
			</ModalBody>
		</ChoreographerModal>
	);
};
